import React, { ReactNode } from 'react'
import {
  IVideo as IContentfulVideo,
  IImage as IContentfulImage,
  IMediaByBreakpoint as IContentfulMediaByBreakpoint,
  IImageWithModalVideo as IContentfulImageWithModalVideo,
} from 'marketing-site/@types/generated/contentful'
import {
  transformAssetToImage,
  transformImage,
} from 'marketing-site/src/transformers/elements/ContentfulImage'
import { IProps as IImage, Image } from 'marketing-site/src/library/elements/Image'
import { VideoAutoplay } from 'marketing-site/src/library/elements/VideoAutoplay'
import { Asset as IContentfulAsset } from 'contentful'
import { transformMediaByBreakpoints } from 'marketing-site/src/transformers/elements/ContentfulMediaByBreakpoint'
import { transformImageWithModalVideo } from 'marketing-site/src/mvp/transformers/elements/ContentfulImageWithModalVideo'
import { MediaByBreakpoint } from 'marketing-site/src/library/elements/MediaByBreakpoint'
import { ImageWithModalVideo } from 'marketing-site/src/mvp/library/elements/ImageWithModalVideo'
export interface IVideo {
  video: string
  fallbackImage: string
  playOnce?: boolean
  autoplay?: boolean
  muted?: boolean
  showControls?: boolean
}

export interface IVideoWithImageProps extends Omit<IVideo, 'fallbackImage'> {
  fallbackImage: IImage
}

export function transformVideo(video: IContentfulVideo): IVideo {
  return {
    video: video.fields.video.fields.file.url,
    fallbackImage: video.fields.fallbackImage.fields.file.url,
    playOnce: !video.fields.loop,
    autoplay: video.fields.autoplay,
    muted: video.fields.muted,
    showControls: video.fields.controls,
  }
}

export function transformVideoWithImageProps(video: IContentfulVideo): IVideoWithImageProps {
  return {
    ...transformVideo(video),
    fallbackImage: {
      url: video.fields.fallbackImage.fields.file.url,
      width: video.fields.fallbackImage.fields.file.details.image?.width,
      height: video.fields.fallbackImage.fields.file.details.image?.height,
    },
  }
}

export const transformMediaContent = (
  media:
    | IContentfulImage
    | IContentfulVideo
    | IContentfulMediaByBreakpoint
    | IContentfulImageWithModalVideo,
  noLazyLoad?: boolean,
): ReactNode | undefined => {
  const noLazyLoaded = noLazyLoad ? noLazyLoad : false

  switch (media.sys.contentType.sys.id) {
    case 'image':
    case 'video':
      return transformMedia(media as IContentfulImage | IContentfulVideo)
    case 'mediaByBreakpoint':
      return transformMediaByBreakpointsContent(media as IContentfulMediaByBreakpoint, noLazyLoaded)
    case 'imageWithModalVideo':
      return transformImageWithModalVideoContent(media as IContentfulImageWithModalVideo)
  }
  return undefined
}

export function transformMedia(
  media: IContentfulImage | IContentfulVideo | IContentfulAsset,
): ReactNode {
  switch (media.sys.contentType?.sys.id) {
    case 'image':
      return <Image {...transformImage(media as IContentfulImage)} />
    case 'asset':
      return <Image {...transformAssetToImage(media as IContentfulAsset)} />
    case 'video':
      return <VideoAutoplay {...transformVideo(media as IContentfulVideo)} />
    default:
      return undefined
  }
}

const transformMediaByBreakpointsContent = (
  media: IContentfulMediaByBreakpoint,
  noLazyLoad?: boolean,
) => {
  if (!media) return
  return <MediaByBreakpoint {...transformMediaByBreakpoints(media, noLazyLoad)} />
}

const transformImageWithModalVideoContent = (media: IContentfulImageWithModalVideo) => {
  if (!media) return
  return <ImageWithModalVideo {...transformImageWithModalVideo(media)} />
}
